import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useAuthStore } from '../../store/authStore';
import { walletApi, builderCreditsApi } from '../../services/platformApi';
import { useWalletModal } from '../../store/walletModal';
import WalletModal from '../WalletModal';
import ProductSwitcher from '../ProductSwitcher';
import { BuilderCreditsBanner } from '@podstack/ui';
import { CONSOLE_NAV } from '../../config/consoleNav';
import {
  CompassIc,
  MenuIc,
  CloseIc,
  LogoutIc,
  CardIc,
  BookIc,
} from '../console/icons';

// Console shell (Oct 2026 redesign). Three widths, same as customer-portal:
//   > 1100px  full 232px sidebar with the group label
//   821-1100  76px icon rail
//   <= 820px  hidden; the mobile bar's menu button opens it full-width
// Icon-rail range classes are spelled out in full so Tailwind's content scan finds them.
const itemBase =
  'flex items-center gap-2.5 min-h-[32px] px-2.5 rounded-md text-sm transition-colors ' +
  `min-[821px]:max-[1100px]:justify-center min-[821px]:max-[1100px]:w-12 min-[821px]:max-[1100px]:min-h-[44px] min-[821px]:max-[1100px]:px-0 ` +
  'max-[820px]:min-h-[44px] max-[820px]:text-[15px]';

const openAdvisor = () => window.dispatchEvent(new CustomEvent('podstack-advisor:open'));

const signOut = async (logout: () => Promise<void>) => {
  await logout();
  // Hard-redirect to the public landing so we leave the protected
  // area cleanly. Navigating within the SPA can momentarily hit the
  // customer login, which would re-issue the session over SSO.
  window.location.assign('/inference/');
};

const useWalletBalance = () => {
  const { data: wallet, isError } = useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletApi.getWallet().then((r) => r.data),
  });
  const balance = wallet?.available_balance ?? wallet?.balance ?? 0;
  return { balance, isError, label: isError ? 'Unavailable' : `₹${balance.toFixed(2)}` };
};

const AccountMenu: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { user, logout } = useAuthStore();
  const showWallet = useWalletModal((s) => s.show);
  const wallet = useWalletBalance();
  const name = user?.name || user?.email || 'Account';
  const initial = name.charAt(0).toUpperCase();
  const item = 'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left text-ps-text data-[focus]:bg-ps-raised';
  return (
    <Menu as="div" className="relative">
      <MenuButton
        aria-label={`Account, ${name}`}
        className={`rounded-full grid place-items-center bg-[#6D4AD8] text-white font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-ps-accent ${
          compact ? 'w-11 h-11' : 'w-10 h-10'
        }`}
      >
        {initial}
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-50 mt-2 w-64 rounded-xl border border-ps-strong bg-ps-card py-1 shadow-xl shadow-black/50 focus:outline-none"
      >
        <div className="border-b border-ps-border px-4 py-3">
          {user?.name && <p className="truncate text-sm font-medium text-ps-text">{user.name}</p>}
          <p className="truncate text-xs text-ps-subtle">{user?.email}</p>
        </div>
        <MenuItem>
          <button type="button" onClick={showWallet} className={item}>
            <CardIc />
            <span className="flex-1">Wallet</span>
            <span className={wallet.isError ? 'text-ps-err' : 'text-ps-muted'}>{wallet.label}</span>
          </button>
        </MenuItem>
        <MenuItem>
          <NavLink to="/console/docs" className={item}>
            <BookIc /> Documentation
          </NavLink>
        </MenuItem>
        <div className="mt-1 border-t border-ps-border pt-1">
          <MenuItem>
            <button type="button" onClick={() => signOut(logout)} className={`${item} text-ps-err`}>
              <LogoutIc /> Sign out
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </Menu>
  );
};

const WalletButton: React.FC = () => {
  const showWallet = useWalletModal((s) => s.show);
  const wallet = useWalletBalance();
  return (
    <button
      type="button"
      onClick={showWallet}
      title="Wallet balance - top up"
      className={`ps-btn2 ${wallet.isError ? 'text-ps-err' : ''}`}
    >
      <span className="text-ps-subtle max-[1100px]:hidden">Wallet</span>
      {wallet.label}
    </button>
  );
};

const ConsoleLayout: React.FC = () => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const mic =
    'w-11 h-11 flex-none grid place-items-center rounded-md border border-ps-strong bg-ps-raised text-ps-text hover:bg-ps-border';

  return (
    <div className="flex min-h-screen bg-ps-page text-ps-text max-[820px]:flex-col">
      {/* Phone-only bar: product switcher, Advisor, account, menu toggle. */}
      <div className="flex items-center gap-2 border-b border-ps-raised bg-ps-chrome px-4 py-2 min-[821px]:hidden">
        <ProductSwitcher compact />
        <span className="flex-1" />
        <button type="button" onClick={openAdvisor} aria-label="Compute Advisor" className={mic}>
          <CompassIc />
        </button>
        <AccountMenu compact />
        <button
          type="button"
          onClick={() => setMobileNavOpen((o) => !o)}
          className={mic}
          aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileNavOpen}
          aria-controls="console-nav"
        >
          {mobileNavOpen ? <CloseIc /> : <MenuIc />}
        </button>
      </div>

      <nav
        id="console-nav"
        aria-label="Primary"
        className={
          'flex flex-col gap-0.5 overflow-y-auto border-ps-raised bg-ps-chrome ' +
          'min-[1101px]:sticky min-[1101px]:top-0 min-[1101px]:h-screen min-[1101px]:w-[232px] min-[1101px]:flex-none min-[1101px]:border-r min-[1101px]:px-3 min-[1101px]:pb-6 min-[1101px]:pt-4 ' +
          `min-[821px]:max-[1100px]:sticky min-[821px]:max-[1100px]:top-0 min-[821px]:max-[1100px]:h-screen min-[821px]:max-[1100px]:w-[76px] min-[821px]:max-[1100px]:flex-none min-[821px]:max-[1100px]:items-center min-[821px]:max-[1100px]:border-r min-[821px]:max-[1100px]:px-2.5 min-[821px]:max-[1100px]:py-4 ` +
          (mobileNavOpen
            ? 'max-[820px]:flex max-[820px]:w-full max-[820px]:border-b max-[820px]:px-4 max-[820px]:pb-4 max-[820px]:pt-2'
            : 'max-[820px]:hidden')
        }
      >
        <div className={`px-1 pb-3 pt-0.5 max-[820px]:hidden min-[821px]:max-[1100px]:px-0`}>
          <ProductSwitcher nameClassName={`min-[821px]:max-[1100px]:hidden`} />
        </div>

        {CONSOLE_NAV.map((g) => (
          <React.Fragment key={g.id}>
            {g.label && (
              <div
                className={
                  'mx-2.5 mb-0.5 mt-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-ps-subtle ' +
                  `min-[821px]:max-[1100px]:mx-0 min-[821px]:max-[1100px]:mb-1.5 min-[821px]:max-[1100px]:mt-2.5 min-[821px]:max-[1100px]:h-px min-[821px]:max-[1100px]:w-8 min-[821px]:max-[1100px]:bg-ps-border min-[821px]:max-[1100px]:text-[0px]`
                }
              >
                {g.label}
                {g.hint && <span className="font-normal normal-case tracking-normal"> · {g.hint}</span>}
              </div>
            )}
            {g.items.map(({ id, name, href, icon: Icon, external }) =>
              external ? (
                <a
                  key={id}
                  id={id}
                  href={href}
                  title={name}
                  className={`${itemBase} text-ps-muted hover:bg-ps-hover hover:text-ps-text`}
                >
                  <Icon />
                  <span className={`min-[821px]:max-[1100px]:sr-only truncate`}>{name}</span>
                </a>
              ) : (
                <NavLink
                  key={id}
                  id={id}
                  to={href}
                  title={name}
                  onClick={() => setMobileNavOpen(false)}
                  className={({ isActive }) =>
                    `${itemBase} ${
                      isActive ? 'bg-ps-accent-soft text-ps-accent' : 'text-ps-muted hover:bg-ps-hover hover:text-ps-text'
                    }`
                  }
                >
                  <Icon />
                  <span className={`min-[821px]:max-[1100px]:sr-only truncate`}>{name}</span>
                </NavLink>
              )
            )}
          </React.Fragment>
        ))}
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex flex-wrap items-center justify-end gap-2.5 border-b border-ps-raised bg-ps-chrome px-7 py-3 max-[820px]:hidden">
          <button type="button" onClick={openAdvisor} className="ps-btn2">
            <CompassIc />
            <span className="max-[1100px]:hidden">Compute Advisor</span>
          </button>
          <WalletButton />
          <AccountMenu />
        </header>

        <main className="relative min-w-0 flex-1">
          <div className="w-full max-w-[1280px] px-4 pt-5 empty:hidden sm:px-7 sm:pt-7">
            <BuilderCreditsBanner
              onApply={async (data) => {
                await builderCreditsApi.apply(data);
              }}
              kycEnabled={process.env.REACT_APP_ENABLE_BUILDER_CREDITS_KYC === 'true'}
            />
          </div>
          <Outlet />
        </main>
      </div>

      {/* Single wallet modal, available on every console page */}
      <WalletModal />
    </div>
  );
};

export default ConsoleLayout;
